import React, {useCallback, useEffect, useMemo, useState} from 'react'
import classnames from 'classnames'
import PropTypes from 'prop-types'

import AccountDropdown from './AccountDropdown'
import Container from '../../lib/Container'
import Dev from '../../dev_only'
import * as Links from '../Links'
import Logo from '../Logo'
import EverplansBrandedCobranding from './EverplansBrandedCobranding'
import GlobalAnnouncementBanner from '../../dashboard/src/components/feedback/GlobalAnnouncementBanner'
import {useOrganizationCobranding} from '../OrganizationCobrandingContext'
import OrganizationCobrandingHeaderLogo from './OrganizationCobrandingHeaderLogo'
import ResourcesGuide from './ResourcesGuide'
import SessionStore from '../../auth/src/stores/SessionStore'
import StylishHatAndTails from '../StylishHatAndTails'

import {
  hasEverplansCobranding,
  hasOrganizationCobranding
} from '../../lib/cobrandingTools'

import './header.scss'

const hasCurrentUserData = currentUser => (
  Boolean(currentUser) && Object.keys(currentUser).length > 0
)

const HeaderController = ({flavor: flavorProp}) => {
  const brandingStore = useMemo(() => Container.getStore('config'), [])
  const headerStore = useMemo(() => Container.getStore('headerFlavor'), [])

  const [flavor, setFlavor] = useState(headerStore.getState().data.flavor)

  const [currentUser, setCurrentUser] = useState(
    SessionStore.getState().currentUser || {}
  )

  const cobranding = useMemo(() => (
    brandingStore.getState().data.cobranding
  ), [brandingStore])

  const organizationCobranding = useOrganizationCobranding()

  useEffect(() => {
    const updateDisplayName = () => {
      const session = SessionStore.getState()
      if (session !== null) setCurrentUser(session.currentUser)
    }

    const updateFlavor = () => setFlavor(headerStore.getState().data.flavor)

    SessionStore.addChangeListener(updateDisplayName)
    headerStore.addChangeListener(updateFlavor)
    updateFlavor()

    return () => {
      SessionStore.removeChangeListener(updateDisplayName)
      headerStore.removeChangeListener(updateFlavor)
    }
  }, [headerStore])

  const getFlavor = useCallback(() => (
    window.location.pathname.match('document-bundles/*') ?
      'guest-user' :
      flavorProp || flavor
  ), [flavor, flavorProp])

  const isLogInOrSignUpFlavor = useCallback(() => {
    const headerFlavor = getFlavor()
    return headerFlavor === 'login' || headerFlavor === 'sign-up'
  }, [getFlavor])

  return (
    <>
      <GlobalAnnouncementBanner
        className={classnames({
          'with-login-or-sign-up-flavor-header': isLogInOrSignUpFlavor()
        })}
      />
      <Header
        cobranding={cobranding}
        currentUser={currentUser}
        flavor={getFlavor()}
        organizationCobranding={organizationCobranding}
      />
    </>
  )
}

HeaderController.propTypes = {
  flavor: PropTypes.oneOf([
    'guest-user',
    'login',
    'sign-up',
    'transactional',
    'app'
  ])
}

export default HeaderController

export class Header extends React.Component {
  classNames() {
    var flavor = this.props.flavor || 'transactional'
    return classnames({
      cobranded: hasEverplansCobranding(this.props.cobranding),
      'with-organization-cobranding': hasOrganizationCobranding(this.props.organizationCobranding),
      [`${flavor}-flavor`]: true
    })
  }

  isLeftAligned() { return ['login', 'guest-user'].includes(this.props.flavor) }

  renderLogo() {
    if (hasOrganizationCobranding(this.props.organizationCobranding)) {
      return (
        <>
          <OrganizationCobrandingHeaderLogo
            organizationCobranding={this.props.organizationCobranding}
          />
          <Logo className='with-organization-cobranding' />
        </>
      )
    }

    if (hasEverplansCobranding(this.props.cobranding)) {
      return (
        <EverplansBrandedCobranding
          cobranding={this.props.cobranding}
          leftAligned={this.isLeftAligned()}
        />
      )
    } else {
      return <Logo />
    }
  }

  render() {
    return (
      <div className='hat-and-tails header-one'>
        <Dev.Title title={this.props.currentUser ? this.props.currentUser.id : null} />
        <StylishHatAndTails cobranding={this.props.cobranding} />
        <header className={this.classNames()}>
          <nav>
            <div className='logo-container'>
              <div className='logo'>
                <a
                  className='logo-link'
                  href={hasCurrentUserData(this.props.currentUser) ? API_HOST : CONTENT_HOST}>
                  {this.renderLogo()}
                </a>
                {
                  this.props.flavor === 'app' &&
                  hasCurrentUserData(this.props.currentUser) &&
                  (
                    <span className='mini-login'>
                      <AccountDropdown
                        currentUser={this.props.currentUser}
                        flavor={this.props.flavor}
                      />
                    </span>
                  )
                }
              </div>
            </div>
            <div className='cobranded-divider' />
            <FlavorMenu
              currentUser={this.props.currentUser}
              flavor={this.props.flavor}
            />
          </nav>
        </header>
      </div>
    )
  }
}

Header.propTypes = {
  cobranding: PropTypes.object,
  currentUser: PropTypes.object,
  flavor: PropTypes.oneOf([
    'guest-user',
    'login',
    'sign-up',
    'transactional',
    'app'
  ]),
  organizationCobranding: PropTypes.shape({
    enabled: PropTypes.bool,
    organizationId: PropTypes.string,
    organizationLogoUrl: PropTypes.string,
    primaryColor: PropTypes.string,
    secondaryColor: PropTypes.string,
    tertiaryColor: PropTypes.string,
    type: PropTypes.string
  })
}

Header.defaultProps = {
  cobranding: {enabled: false},
  currentUser: {},
  flavor: 'app'
}

const FlavorMenu = props => {
  switch (props.flavor) {
    case 'login':
      return <LoginMenu {...props} />
    case 'app':
      return <Menu {...props} />
    case 'guest-user':
      return <GuestUserMenu {...props} />
    case 'sign-up':
      return <SignUpMenu />
    case 'transactional':
    default:
      return <TransactionalMenu {...props} />
  }
}

FlavorMenu.propTypes = {
  flavor: PropTypes.oneOf(['guest-user', 'login', 'sign-up', 'transactional', 'app'])
}

const LoginMenu = () => (
  <div className='menu'>
    <div className='prompt'>Already have an account?</div>
    <Links.LogIn />
  </div>
)

const GuestUserMenu = () => (
  <div className='menu'>
    <div className='everplans-motto'>A new way to plan for the future.</div>
  </div>
)

const SignUpMenu = () => (
  <div className='menu'>
    <div className='prompt'>Need an account?</div>
    <Links.SignUp />
  </div>
)

const TransactionalMenu = () => <span />

const Menu = props => {
  if (!hasCurrentUserData(props.currentUser))
    return <span />

  var user = props.currentUser ? props.currentUser : {}
  return (
    <div className='menu-container'>
      <div className='menu'>
        <Links.MyPersonalEverplan currentUser={props.currentUser} />
        <ResourcesGuide />
        <AccountDropdown currentUser={user} />
      </div>
    </div>
  )
}

Menu.propTypes = {currentUser: PropTypes.object}
